import { AxiosError } from 'axios';
import i18n from '@/i18n';
import { del, get, post, put } from '@/utils';
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';
import { IProjectStatus, IProjectStatusPayload } from './types';

const BASE = 'api/project-stages';

type RawStatus = {
	id?: number | string;
	name?: string;
	color?: string;
	position?: number;
	default?: boolean;
	description?: string | null;
	createdAt?: string;
};

type StatusesResponse = {
	items?: RawStatus[];
	totalPages?: number;
	totalCount?: number;
};

const normalizeStatus = (raw: RawStatus, index: number): IProjectStatus => ({
	id: raw.id ?? index,
	name: String(raw.name || ''),
	color: raw.color || undefined,
	isDefault: Boolean(raw.default),
	position: typeof raw.position === 'number' ? raw.position : index,
	description: raw.description ?? undefined,
	createdAt: raw.createdAt ?? undefined
});

const buildQuery = (query: string, page: number, items: number): string => {
	const params = new URLSearchParams((query || '').replace(/^\?/, ''));
	params.set('PageNumber', String(page + 1));
	params.set('PageSize', String(items));
	return `?${params.toString()}`;
};

// BE → payload: câmpul e `default` (nu `isDefault`).
const toPayload = (payload: IProjectStatusPayload): Record<string, unknown> => ({
	name: payload.name,
	color: payload.color,
	default: payload.isDefault ?? false,
	description: payload.description
});

async function fetchStatuses(
	query: string,
	page: number,
	items: number,
	onResponse?: (totalPages: number, totalCount: number) => void
): Promise<IProjectStatus[]> {
	try {
		const res = await get(`${BASE}${buildQuery(query, page, items)}`);
		const data = res.data as StatusesResponse;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return (data.items || []).map(normalizeStatus);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectStages.errorLoad'));
	}
}

async function postStatus(payload: IProjectStatusPayload): Promise<string | number> {
	try {
		const res = await post(BASE, toPayload(payload));
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectStages.errorCreate'));
	}
}

async function patchStatus(
	statusId: number | string,
	payload: IProjectStatusPayload
): Promise<string | number> {
	try {
		const res = await put(`${BASE}/${statusId}`, toPayload(payload));
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectStages.errorUpdate'));
	}
}

async function deleteStatus(statusId: number | string): Promise<string | number> {
	try {
		const res = await del(`${BASE}/${statusId}`);
		return res?.status && res.status >= 200 && res.status < 300 ? res.status : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectStages.errorDelete'));
	}
}

// GET api/project-stages/table-config — configurația coloanelor tabelului Stagii de
// proiect (chei, etichete, ordine, vizibilitate/sortabilitate implicite). Se corelează
// în pagină cu definițiile locale prin applyServerColumnConfig.
async function fetchStatusesTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get(`${BASE}/table-config`);
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să încărcăm configurația tabelului.');
	}
}

export { fetchStatuses, fetchStatusesTableConfig, postStatus, patchStatus, deleteStatus };
